import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { browserHistory } from 'react-router';
import AutoComplete from 'material-ui/AutoComplete';
import { formatDateOfBirth } from 'ui-utils';

import MenuItem from 'material-ui/MenuItem';
import { map, take, debounce, get, replace, join } from 'lodash';
import { withStyles } from '@material-ui/core';
import { Search as SearchIcon, Block as BlockIcon, Assignment as AssignmentIcon } from '@material-ui/icons';

import PatientAvatar from 'components/Patient/PatientAvatar';
import FormattedSimpleMessage from 'core/FormattedSimpleMessage';
import { isValidPhoneNumber } from 'utils/validation';

import { muiThemeLuma } from 'themes';

import moment from 'moment';

import {
  WAITLIST,
  APPT,
  REMINDER,
  REFERRAL,
  OUTBOUND_REFERRAL,
  FORM,
  OFFER,
  LUMABOT_FLOW,
} from 'containers/Header/constants';

import messages from './messages';
import { phoneMaskUS, phoneOnlyNumbers, highlightText, highlightNumbers, highlightDOB } from './utils';

const styles = (theme) => {
  const {
    typography,
    custom: {
      palette: { colorSupportSteel, colorAccentWatermelon },
    },
    spacing: { unit },
  } = theme;

  return {
    mongoMenuItemContent: {
      display: 'flex',
      alignItems: 'center',
    },
    menuItemContent: {
      flexGrow: 1,
      lineHeight: '21px',
    },
    menuItemContact: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    patientAvatar: {
      paddingRight: unit,
      width: 55,
      alignSelf: 'center',
    },
    menuItemGenericContent: {
      lineHeight: '21px',
      width: '90%',
    },
    menuItemGenericContentHeader: {
      display: 'inline-block',
      width: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      verticalAlign: 'bottom',
      fontSize: '15px',
    },
    menuItemGenericDetails: {
      display: 'flex',
      justifyContent: 'flex-start',

      '& span': {
        fontSize: '12px',
      },
    },
    genericAvatar: {
      fontSize: 32,
      marginRight: 2 * unit,
      color: colorSupportSteel,
    },
    menuItemMongoId: {
      fontWeight: 700,
      fontFamily: 'monospace',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      verticalAlign: 'bottom',
    },
    searchIcon: {
      color: colorSupportSteel,
      float: 'left',
      fontSize: 16,
      lineHeight: 16,
      height: 60,
    },
    root: {
      display: 'inline-block',
      width: 'calc(100% - 347px)',
    },
    patientSearchContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignSelf: 'center',
      alignItems: 'center',
      justifyContent: 'space-between',
      border: `2px solid ${muiThemeLuma.custom.palette.colorSupportPowder}`,
      backgroundColor: 'white',
      borderRadius: 3,
      paddingRight: unit,
      width: 424,
      height: 40,
    },
    doNotContact: {
      color: colorAccentWatermelon,
      display: 'flex',
      alignItems: 'center',
    },
    doNotContactIcon: {
      color: colorAccentWatermelon,
      float: 'left',
      fontSize: 13,
    },
  };
};

const stylesInline = {
  autoComplete: {
    height: 60,
    width: 'calc(100% - 100px)',
  },
  hint: {
    bottom: 20,
    fontSize: 15,
    letterSpacing: '-0.1px',
    fontWeight: 300,
    lineHeight: '22px',
    width: '100%',
    color: muiThemeLuma.custom.palette.colorSupportSteel,
  },
  input: {
    fontSize: 15,
    fontWeight: 500,
    height: 60,
    paddingLeft: 8,
    width: 380,
  },
  menu: {
    maxHeight: 392,
    overflowX: 'hidden',
    overflowY: 'auto',
    width: '100%',
  },
  menuList: {
    paddingBottom: 0,
    paddingTop: 0,
    width: '100%',
  },
  menuItem: {
    width: 420,
    padding: '4px 0',
  },
  underline: {
    display: 'none',
  },
};

const initialState = {
  searchText: '',
};

const MAX_ITEMS_DISPLAYED = 7;
const LINK_PATIENTS_TEXT = 'Select multiple patients';

const parseSearchText = (searchText) => {
  const searchTextWithoutWhiteSpaces = searchText.replace(/\s/g, '');
  // I don't know why, but isValidPhoneNumber returns false to valid US numbers, so we need to call it as !isValidPhoneNumber
  return !isValidPhoneNumber(searchTextWithoutWhiteSpaces) ? searchTextWithoutWhiteSpaces : searchText;
};

class GlobalSearch extends PureComponent {
  state = initialState;

  handleFilter = (searchText, key) => {
    // always show link to /patients

    if (key === LINK_PATIENTS_TEXT) return true;

    // always show all results from searches by id
    if (/^(ids?:)/.test(searchText)) return true;

    return AutoComplete.caseInsensitiveFilter(parseSearchText(searchText), key);
  };

  handleSelectedPatient = ({ _id }) => {
    browserHistory.push(_id ? `/patients/${_id}/chat` : '/patients');
  };

  handleSearchDebounced = debounce((value) => {
    const { onSearch } = this.props;

    onSearch(parseSearchText(value));
  }, 300);

  handleUpdateInput = (searchText, dataSource, { source }) => {
    this.setState({ searchText });

    if (source === 'click') {
      this.setState(initialState);
      this.handleSearchDebounced('');
    } else if (searchText && searchText.length > 2) {
      this.handleSearchDebounced(searchText);
    }
  };

  renderPatientAvatar(patient) {
    const { classes } = this.props;

    return (
      <div className={classes.patientAvatar}>
        <PatientAvatar item={patient} />
      </div>
    );
  }

  renderMenuItemLink() {
    const { data } = this.props;
    return (
      <MenuItem
        style={{
          ...stylesInline.menuItem,
          textAlign: 'center',
          borderTop: data.length > 0 ? `1px inset ${muiThemeLuma.custom.palette.colorSupportSteel}` : 'none',
        }}
        key="link"
        primaryText={LINK_PATIENTS_TEXT}
        onClick={this.handleSelectedPatient}
      />
    );
  }

  handleMenuItemHeader() {
    const { data } = this.props;
    const { type, patient, name, patientFormTemplateName, date, status, providers } = data;

    switch (type) {
      case WAITLIST:
        return `${patient} - ${join(providers, ', ')}`;
      case APPT:
        return `${patient} - ${moment(date).format('LLL')}`;
      case REMINDER: {
        const captilizedStatus = replace(status, /^./, (char) => char.toUpperCase());
        return `${patient} - ${captilizedStatus}`;
      }
      case LUMABOT_FLOW:
      case FORM:
        return `${patient} - ${patientFormTemplateName}`;
      case REFERRAL:
      case OUTBOUND_REFERRAL:
        return `${patient}`;
      default:
        return `${name}`;
    }
  }

  // A mongo search is when one queries by mongo id
  renderMenuItemForMongoIdSearch() {
    const { classes, data } = this.props;
    const { _id, type, resourceUrl } = data;

    return (
      <MenuItem
        data-lh-id="global-search-result-item"
        style={stylesInline.menuItem}
        key={_id}
        onClick={() => browserHistory.push(resourceUrl)}
      >
        <div className={classes.mongoMenuItemContent}>
          <div>
            <AssignmentIcon className={classes.genericAvatar} />
          </div>
          <div className={classes.menuItemGenericContent}>
            {type !== OFFER && (
              <span className={classes.menuItemGenericContentHeader}>{this.handleMenuItemHeader()}</span>
            )}
            <div className={classes.menuItemGenericDetails}>
              <span>{type}</span>
              &nbsp;-&nbsp;
              <span className={classes.menuItemMongoId}>{_id}</span>
            </div>
          </div>
        </div>
      </MenuItem>
    );
  }

  // A default search is when one queries by patient name,dob or phone number
  renderMenuItemForDefaultSearch(patient, name, index) {
    const { classes, localization } = this.props;
    const { searchText } = this.state;
    const { _id, dateOfBirth, contact, doNotContact } = patient;

    let dob = '';
    let patientPhoneNumber;

    if (dateOfBirth) {
      dob = formatDateOfBirth(dateOfBirth, localization);
    }

    // the priority is to show the active SMS contact.
    if (contact && contact.length > 0) {
      // get the first active and the first inactive SMS contact
      const patientActiveSMSContact = contact.find(
        (contactMethod) => contactMethod.active && contactMethod.type === 'sms',
      );
      const patientInactiveSMSContact = contact.find(
        (contactMethod) => !contactMethod.active && contactMethod.type === 'sms',
      );

      // get the first active and the first inactive voice contact
      const patientActiveVoiceContact = contact.find(
        (contactMethod) =>
          contactMethod.active && (contactMethod.type === 'voice' || contactMethod.type === 'whatsapp'),
      );
      const patientInactiveVoiceContact = contact.find(
        (contactMethod) =>
          !contactMethod.active && (contactMethod.type === 'voice' || contactMethod.type === 'whatsapp'),
      );

      // we prioritize to show the active phone contact, always looking for SMS first
      const patientActiveContact = get(patientActiveSMSContact, 'value') || get(patientActiveVoiceContact, 'value');
      const patientInactiveContact =
        get(patientInactiveSMSContact, 'value') || get(patientInactiveVoiceContact, 'value');
      const patientContactNumber = patientActiveContact || patientInactiveContact;

      if (patientContactNumber) {
        patientPhoneNumber = phoneMaskUS(patientActiveContact || patientInactiveContact);
      }
    }

    const patientName = highlightText(name, searchText);
    const patientPN = highlightNumbers(patientPhoneNumber, searchText);
    const patientDOB = highlightDOB(dob, searchText);

    return (
      <MenuItem
        data-lh-id={`patient-search-result-${index}-item`}
        style={stylesInline.menuItem}
        key={_id}
        onClick={() => this.handleSelectedPatient(patient)}
      >
        <div style={{ display: 'flex' }}>
          {this.renderPatientAvatar(patient)}
          <div className={classes.menuItemContent}>
            {/* eslint-disable-next-line react/no-danger */}
            <span dangerouslySetInnerHTML={{ __html: patientName }} />
            <br />
            <div className={classes.menuItemContact}>
              {/* eslint-disable-next-line react/no-danger */}
              <span dangerouslySetInnerHTML={{ __html: patientDOB }} />
              {patientPN && this.renderContact(patientPN, doNotContact)}
            </div>
            {searchText === _id && <div className={classes.menuItemMongoId}>{_id}</div>}
          </div>
        </div>
      </MenuItem>
    );
  }

  renderContact = (phoneNumber, doNotContact) => {
    const { classes } = this.props;

    return doNotContact ? (
      <div className={classes.doNotContact}>
        <BlockIcon className={classes.doNotContactIcon} />
        {/* eslint-disable-next-line react/no-danger */}
        <span dangerouslySetInnerHTML={{ __html: phoneNumber }} />
      </div>
    ) : (
      // eslint-disable-next-line react/no-danger
      <span dangerouslySetInnerHTML={{ __html: phoneNumber }} />
    );
  };

  renderDataSource() {
    const { data, localization } = this.props;

    if (data.queryType === 'mongoId') {
      const result = [
        {
          text: `${data._id}`,
          value: this.renderMenuItemForMongoIdSearch(),
        },
      ];

      return result;
    }

    const result = map(take(data, MAX_ITEMS_DISPLAYED), (patient, index) => {
      const { firstname, lastname, externalId, secondaryExternalId, contact, dateOfBirth, _id } = patient;
      let patientPhoneSearchOptions = [];

      if (contact && contact.length > 0) {
        const patientContacts = contact.filter(
          (contactMethod) =>
            contactMethod.type === 'sms' || contactMethod.type === 'voice' || contactMethod.type === 'whatsapp',
        );
        // getting all contact phone numbers, even if the patient is marked as DNC or the contact is inactive to help the user to find the patient.
        patientPhoneSearchOptions = patientContacts.map((phoneNumber) => {
          const phoneNumberDefault = phoneNumber.value;
          const phoneNumberUSFormat = phoneMaskUS(phoneNumber.value);
          const phoneNumberOnlyNumbers = phoneOnlyNumbers(phoneNumber.value);
          const phoneNumberWithoutDash = phoneMaskUS(phoneNumber.value, 'withoutDash');
          const phoneNumberOnlyDash = phoneMaskUS(phoneNumber.value, 'onlyDash');
          return `${phoneNumberDefault} ${phoneNumberUSFormat} ${phoneNumberOnlyNumbers} ${phoneNumberWithoutDash} ${phoneNumberOnlyDash}`;
        });
      }

      let dobDefault;
      let dobSecondaryFormat;
      if (dateOfBirth) {
        dobDefault = formatDateOfBirth(dateOfBirth, localization);
        dobSecondaryFormat = moment(`${dobDefault}`, 'MM-DD-YYYY').format('MM/DD/YYYY');
      }

      const name = `${firstname} ${lastname}`;
      const nameUS = `${lastname}, ${firstname}`;
      const nameUS2 = `${lastname},${firstname}`;
      const externalIdStr = externalId && externalId.value ? externalId.value : '';
      const secondaryExternalIdStr = secondaryExternalId && secondaryExternalId.value ? secondaryExternalId.value : '';
      const externalIdValues = `id:${externalIdStr} id: ${externalIdStr} id:  ${externalIdStr} id:   ${externalIdStr}`;
      const secondaryExternalIdValues = `id:${secondaryExternalIdStr} id: ${secondaryExternalIdStr} id:  ${secondaryExternalIdStr} id:   ${secondaryExternalIdStr}`;

      return {
        text: `${externalIdValues} ${secondaryExternalIdValues} ${_id} ${name} ${nameUS} ${nameUS2} ${dobDefault} ${dobSecondaryFormat} ${patientPhoneSearchOptions.map(
          (value) => value,
        )}`,
        value: this.renderMenuItemForDefaultSearch(patient, name, index),
      };
    });

    result.push({
      text: LINK_PATIENTS_TEXT,
      value: this.renderMenuItemLink(),
    });

    return result;
  }

  render() {
    const { classes } = this.props;
    const { searchText } = this.state;

    return (
      <div className={classes.root}>
        <div className={classes.patientSearchContainer}>
          <AutoComplete
            dataSource={this.renderDataSource()}
            filter={this.handleFilter}
            hintStyle={stylesInline.hint}
            hintText={<FormattedSimpleMessage {...messages.hintText} />}
            listStyle={stylesInline.menuList}
            data-lh-id="global-search-search-field-input"
            menuStyle={stylesInline.menu}
            onUpdateInput={this.handleUpdateInput}
            openOnFocus
            searchText={searchText}
            style={stylesInline.autoComplete}
            textFieldStyle={stylesInline.input}
            underlineStyle={stylesInline.underline}
          />
          <SearchIcon className={classes.searchIcon} />
        </div>
      </div>
    );
  }
}

GlobalSearch.propTypes = {
  classes: PropTypes.object.isRequired,
  onSearch: PropTypes.func.isRequired,
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.bool, PropTypes.object]),
  localization: PropTypes.string,
};

export default withStyles(styles)(GlobalSearch);
